import React from "react";

import { BillingProvider } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/billing_provider_pb";

import {
  ACCESS_CARD,
  BILLING_CARD,
  IDENTITY_CARD,
  LINKS_CARD,
  ORG_DETAILS_CARD,
  PLAN_STATUS_CARD,
  SEATS_CARD,
  SUBSCRIPTION_DETAILS_CARD,
  TERM_CARD,
  USAGE_CARD,
} from "../../../constants/org-dashboard";
import OrgDetails, {
  OrgDetailsProps,
} from "../details-display-cards/org-details";
import SubscriptionDetails from "../details-display-cards/subscription-details";
import SubscriptionDetailsZuora from "../details-display-cards/subscription-details-zuora";
import ProvisionSupportUserButton from "../tabs/user-actions/provision-support-user-button";
import {
  AccessSecurityCard,
  ElsewhereCard,
  IdentityCard,
  UsageCard,
} from "./cards/org-cards";
import {
  BillingCard,
  PlanStatusCard,
  SeatsCard,
  TermCard,
} from "./cards/subscription-cards";

/**
 * Everything the dashboard can show, keyed by the id stored in a layout.
 *
 * The focused cards read straight from the organisation contexts, so they can
 * be placed anywhere without prop plumbing. The two original panes stay in the
 * catalogue as "full" cards: every action they hold — edit dialogs, provisioning,
 * suspension — remains one click away while the focused cards cover reading.
 *
 * A card the user has no permission for should simply be absent from this list,
 * which keeps it out of both the canvas and the "add card" menu.
 */
export interface DashboardCardContext extends Omit<
  OrgDetailsProps,
  "refreshOrgData"
> {
  billingProvider?: BillingProvider;
  refreshOrgData: () => void;
}

export interface DashboardCardDefinition {
  id: string;
  /** Shown in the "add card" menu; each card renders its own heading. */
  label: string;
  /** Full panes carry their own padded Paper, so the shell must not double it. */
  bare?: boolean;
  render: (context: DashboardCardContext) => React.ReactNode;
}

export const cardCatalogue: DashboardCardDefinition[] = [
  {
    id: IDENTITY_CARD,
    label: "Structure & identity",
    render: ({ regionCode, orgSettings }) => (
      <IdentityCard
        regionCode={regionCode}
        orgSettings={orgSettings}
        action={<ProvisionSupportUserButton regionCode={regionCode} />}
      />
    ),
  },
  {
    id: ACCESS_CARD,
    label: "Access & security",
    render: ({
      regionCode,
      orgSettings,
      ssoData,
      scimData,
      refreshOrgData,
    }) => (
      <AccessSecurityCard
        regionCode={regionCode}
        orgSettings={orgSettings}
        ssoData={ssoData}
        scimData={scimData}
        refreshOrgData={refreshOrgData}
      />
    ),
  },
  {
    id: USAGE_CARD,
    label: "Usage",
    render: ({ regionCode, orgSettings }) => (
      <UsageCard regionCode={regionCode} orgSettings={orgSettings} />
    ),
  },
  { id: LINKS_CARD, label: "Miscellaneous", render: () => <ElsewhereCard /> },
  {
    id: PLAN_STATUS_CARD,
    label: "Plan & status",
    render: () => <PlanStatusCard />,
  },
  {
    id: SEATS_CARD,
    label: "Seats",
    render: ({ refreshOrgData }) => (
      <SeatsCard refreshOrgData={refreshOrgData} />
    ),
  },
  { id: BILLING_CARD, label: "Billing", render: () => <BillingCard /> },
  { id: TERM_CARD, label: "Term", render: () => <TermCard /> },
  {
    id: ORG_DETAILS_CARD,
    label: "Organisation details (full)",
    bare: true,
    render: ({
      regionCode,
      ssoData,
      scimData,
      orgSettings,
      refreshOrgData,
    }) => (
      <OrgDetails
        regionCode={regionCode}
        ssoData={ssoData}
        scimData={scimData}
        orgSettings={orgSettings}
        refreshOrgData={refreshOrgData}
      />
    ),
  },
  {
    id: SUBSCRIPTION_DETAILS_CARD,
    label: "Subscription details (full)",
    bare: true,
    render: ({ billingProvider, refreshOrgData }) =>
      billingProvider === BillingProvider.ZUORA ? (
        <SubscriptionDetailsZuora refreshOrgData={refreshOrgData} />
      ) : (
        <SubscriptionDetails refreshOrgData={refreshOrgData} />
      ),
  },
];

export const cardCatalogueIds = cardCatalogue.map((card) => card.id);

export const findCard = (id: string): DashboardCardDefinition | undefined =>
  cardCatalogue.find((card) => card.id === id);
